import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { monthLabel } from '../utils/dateHelpers';
import { buildMonthlySummaryInsights } from '../utils/monthlySummaryInsights';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('en-GB');
};

const ensureSpace = (doc, y, requiredHeight, margin, pageHeight) => {
  if (y + requiredHeight <= pageHeight - 16) {
    return y;
  }
  doc.addPage();
  return margin;
};

const drawSectionTitle = (doc, title, x, y) => {
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(41, 128, 185);
  doc.text(title, x, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
};

const drawNarrative = (doc, paragraphs, margin, startY, pageWidth, pageHeight) => {
  let y = startY;
  drawSectionTitle(doc, 'Executive Narrative', margin, y);
  y += 6;

  paragraphs.forEach((paragraph) => {
    const lines = doc.splitTextToSize(paragraph, pageWidth - (margin * 2));
    y = ensureSpace(doc, y, (lines.length * 5) + 4, margin, pageHeight);
    doc.setFontSize(10);
    doc.text(lines, margin, y);
    y += (lines.length * 5) + 3;
  });

  return y + 2;
};

const drawKpiRow = (doc, insights, margin, startY, pageWidth) => {
  const cards = [
    ['Total Closed', String(insights.totalClosed)],
    ['Preventive', String(insights.preventiveCount)],
    ['Service Requests', String(insights.serviceRequestCount)],
  ];
  const gap = 4;
  const cardWidth = (pageWidth - (margin * 2) - (gap * 2)) / 3;

  cards.forEach(([label, value], index) => {
    const x = margin + (index * (cardWidth + gap));
    doc.setFillColor(247, 250, 252);
    doc.setDrawColor(220, 227, 234);
    doc.roundedRect(x, startY, cardWidth, 20, 2, 2, 'FD');
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label.toUpperCase(), x + 3, startY + 6);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text(value, x + 3, startY + 15);
    doc.setFont(undefined, 'normal');
  });

  doc.setTextColor(0, 0, 0);
  return startY + 26;
};

const drawHorizontalBarChart = (doc, title, rows, margin, startY, pageWidth, pageHeight, color) => {
  let y = ensureSpace(doc, startY, 28 + (Math.min(rows.length, 6) * 16), margin, pageHeight);
  drawSectionTitle(doc, title, margin, y);
  y += 6;

  if (!rows.length) {
    doc.setFontSize(9);
    doc.text('No data available.', margin, y + 4);
    return y + 10;
  }

  const max = rows[0].count || 1;
  const barX = margin;
  const barWidth = pageWidth - (margin * 2) - 10;

  rows.slice(0, 6).forEach((row) => {
    const labelLines = doc.splitTextToSize(row.label, pageWidth - (margin * 2) - 16);
    const labelHeight = labelLines.length * 4;
    const rowHeight = labelHeight + 9;
    y = ensureSpace(doc, y, rowHeight, margin, pageHeight);
    doc.setFontSize(9);
    doc.setTextColor(31, 41, 55);
    doc.text(labelLines, margin, y + 4);
    doc.setTextColor(75, 85, 99);
    doc.text(String(row.count), margin + barWidth + 4, y + labelHeight + 7, { align: 'right' });
    doc.setFillColor(235, 241, 245);
    doc.roundedRect(barX, y + labelHeight + 2, barWidth, 5, 1, 1, 'F');
    doc.setFillColor(color[0], color[1], color[2]);
    doc.roundedRect(barX, y + labelHeight + 2, (row.count / max) * barWidth, 5, 1, 1, 'F');
    doc.setTextColor(0, 0, 0);
    y += rowHeight;
  });

  return y + 2;
};

const drawTimelineTable = (doc, insights, margin, startY, pageWidth) => {
  autoTable(doc, {
    startY,
    head: [['Day', 'Closed Total', 'Preventive', 'Service Requests']],
    body: insights.timelineRows.length
      ? insights.timelineRows.map((row) => [row.label, String(row.total), String(row.preventive), String(row.serviceRequests)])
      : [['-', '0', '0', '0']],
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    bodyStyles: {
      textColor: [31, 41, 55],
      lineColor: [229, 231, 235],
      lineWidth: 0.3,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    margin: { top: margin, bottom: 16, left: margin, right: margin },
    tableWidth: pageWidth - (margin * 2),
  });

  return doc.lastAutoTable.finalY + 8;
};

const drawDetailTable = (doc, title, head, body, margin, startY, pageWidth) => {
  startY = ensureSpace(doc, startY, 30, margin, doc.internal.pageSize.getHeight());
  drawSectionTitle(doc, title, margin, startY);
  autoTable(doc, {
    startY: startY + 4,
    head: [head],
    body: body.length ? body : [[{ content: 'No records', colSpan: head.length }]],
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    rowPageBreak: 'avoid',
    headStyles: {
      fillColor: [100, 116, 139],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    bodyStyles: {
      textColor: [31, 41, 55],
      lineColor: [229, 231, 235],
      lineWidth: 0.3,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    margin: { top: margin, bottom: 16, left: margin, right: margin },
    tableWidth: pageWidth - (margin * 2),
  });

  return doc.lastAutoTable.finalY + 8;
};

export const buildMonthlySummaryDoc = (monthlySummary) => {
  if (!monthlySummary) return null;

  const insights = buildMonthlySummaryInsights(monthlySummary);
  if (!insights) return null;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let currentY = 42;

  doc.setFillColor(41, 128, 185);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('QDF MONTHLY SUMMARY', margin, 15);
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.text(monthLabel(monthlySummary.year, monthlySummary.month), margin, 24);
  doc.text(`Status scope: ${monthlySummary.statusFilter || 'Closed'}`, margin, 30);
  doc.setTextColor(0, 0, 0);

  currentY = drawKpiRow(doc, insights, margin, currentY, pageWidth);
  currentY = drawNarrative(doc, [insights.scopeNote, ...insights.narrative], margin, currentY, pageWidth, pageHeight);
  currentY = drawDetailTable(doc, 'Service Delivery and Music Management',
    ['Measure', 'Result', 'Details'],
    insights.serviceDeliveryRows.map((row) => [row.label, row.value, row.note]),
    margin, currentY, pageWidth);

  currentY = drawHorizontalBarChart(doc, 'Closed Workload by Location', insights.locationRows, margin, currentY, pageWidth, pageHeight, [37, 99, 235]);
  currentY = drawHorizontalBarChart(doc, 'Priority Mix', insights.priorityRows, margin, currentY, pageWidth, pageHeight, [244, 63, 94]);
  currentY = drawHorizontalBarChart(doc, 'Preventive Program Mix', insights.ppmRows, margin, currentY, pageWidth, pageHeight, [5, 150, 105]);
  currentY = drawHorizontalBarChart(
    doc,
    'Top Service Request Themes',
    insights.srRows.map((row) => ({ label: row.description, count: row.count })),
    margin,
    currentY,
    pageWidth,
    pageHeight,
    [245, 158, 11]
  );

  currentY = drawDetailTable(doc, 'Preventive Inspection Results',
    ['Check Performed', 'Pass', 'Fail', 'N/A', 'No Result'],
    insights.evidence.checklistRows.map((row) => [row.description, row.Pass, row.Fail, row['N/A'], row.Unrecorded]),
    margin, currentY, pageWidth);
  currentY = drawDetailTable(doc, 'Recorded Failed Checks (follow-up status not tracked)',
    ['SC Number', 'Shop / Zone', 'Failed Check', 'Remarks'],
    insights.evidence.findings.map((row) => [row.scNumber, row.shopName, row.description, row.remarks]),
    margin, currentY, pageWidth);
  currentY = drawDetailTable(doc, 'Spare Parts Recorded During PM',
    ['SC Number', 'Shop / Zone', 'Device', 'Part / Description', 'Quantity', 'Remarks'],
    insights.evidence.spareParts.map((row) => [row.scNumber, row.shopName, row.deviceName || '-', row.description || '-', row.quantity || '-', row.remarks || '-']),
    margin, currentY, pageWidth);
  if (insights.evidence.comments.length) {
    currentY = drawDetailTable(doc, 'Technician Observations',
      ['SC Number', 'Shop / Zone', 'Observation'],
      insights.evidence.comments.map((row) => [row.scNumber, row.shopName, row.comments]),
      margin, currentY, pageWidth);
  }

  currentY = ensureSpace(doc, currentY, 30, margin, pageHeight);
  drawSectionTitle(doc, 'Closed Work Orders by Creation Day (UTC)', margin, currentY);
  currentY = drawTimelineTable(doc, insights, margin, currentY + 4, pageWidth);

  currentY = ensureSpace(doc, currentY, 20, margin, pageHeight);
  currentY = drawDetailTable(
    doc,
    'PM Service Checks',
    ['Shop', 'Category', 'SC Number', 'Location / Zone', 'PPM Type', 'Service Check Time', 'Checked By'],
    insights.preventiveShopRows.map((row) => [
      row.shopName || '-',
      row.shopCategory || row.shopLabel || '-',
      row.scNumber || '-',
      `${row.location || '-'}${row.zone ? ` / ${row.zone}` : ''}`,
      row.ppmType || '-',
      formatDateTime(row.completedAt),
      row.completedByUser || '-',
    ]),
    margin,
    currentY,
    pageWidth
  );

  currentY = ensureSpace(doc, currentY, 20, margin, pageHeight);
  drawDetailTable(
    doc,
    'Service Request Summary',
    ['Closed Time', 'Shop', 'Category', 'Closed By', 'SC Number', 'Request / Work Description'],
    insights.serviceRequestSummaryRows.map((row) => [
      formatDateTime(row.closedAt || row.actualResolution),
      row.shopName || row.assetTag || row.location || '-',
      row.shopCategory || row.shopLabel || '-',
      row.closedBy || '-',
      row.scNumber || '-',
      row.description || '-',
    ]),
    margin,
    currentY,
    pageWidth
  );

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  }

  return doc;
};

export const exportMonthlySummaryPdf = (monthlySummary) => {
  const doc = buildMonthlySummaryDoc(monthlySummary);
  if (!doc) return;
  doc.save(`QDF_Monthly_Summary_${monthlySummary.year}_${String(monthlySummary.month).padStart(2, '0')}.pdf`);
};
